/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { UserIconOutline } from '../icons';
import { Typography } from '../typography';

type SizeType = AvatarProps['size'];
type StatusType = NonNullable<AvatarProps['status']>;

interface CircleProperties {
  /** Center coordinates of the circles */
  cxAndCy: number;
  /** Diameter of the circles */
  diameter: number;
  /** Radius of the inner circle */
  rInner: number;
  /** Radius of the outer circle */
  rOuter: number;
}

function circleSizeFactory(size: SizeType): CircleProperties {
  switch (size) {
    case 'x-small':
    case 'small':
    case 'medium':
      return {
        cxAndCy: 4.5,
        diameter: 9,
        rInner: 3,
        rOuter: 4.5,
      };
    case 'large':
    case 'x-large':
    default:
      return {
        cxAndCy: 6,
        diameter: 12,
        rInner: 4,
        rOuter: 6,
      };
  }
}

function StatusIndicator({
  size,
  status,
}: {
  size: SizeType;
  status: StatusType;
}) {
  const { cxAndCy, diameter, rInner, rOuter } = circleSizeFactory(size);
  const statusClasses = `ndl-avatar-status ndl-avatar-status-${status}`;

  return (
    <div
      className={statusClasses}
      title={status.charAt(0).toUpperCase() + status.slice(1)}
      role="presentation"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height={diameter}
        width={diameter}
        viewBox={`0 0 ${diameter} ${diameter}`}
        fill="none"
      >
        <circle
          cx={cxAndCy}
          cy={cxAndCy}
          r={rOuter}
          className="ndl-avatar-status-circle-outer"
        />
        <circle
          cx={cxAndCy}
          cy={cxAndCy}
          r={status === 'unknown' ? rInner - 1 : rInner}
          className="ndl-avatar-status-circle-inner"
        />
      </svg>
    </div>
  );
}

type AvatarProps = {
  /** Whether to display a status indicator */
  hasStatus?: boolean;
  /** Whether the avatar is disabled. When disabled, status indicators are hidden and `onClick` handlers are disabled when cast as a button */
  isDisabled?: boolean;
  /** The name associated with the avatar. Used for alt text and aria-label. For letters type, this text is displayed */
  name?: string;
  /** The shape of the avatar */
  shape?: 'circle' | 'square';
  /** The size of the avatar */
  size?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
  /** Source URI for the image. When provided with `type="image`", displays the image */
  source?: string;
  /** The status to display. Only shown when `hasStatus` is `true` and component is not disabled */
  status?: 'offline' | 'online' | 'unknown';
  /** The type of avatar content. If `type="image"` and no source is provided, automatically falls back to `icon` */
  type?: 'icon' | 'image' | 'letters';
} & (
  | {
      as: 'button';
      onClick: () => void;
    }
  | {
      as?: React.ElementType;
    }
);

const getLettersDisplay = (displayName: string) => {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) {
    return displayName.trim();
  }
  return words
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
};

export const Avatar = <T extends React.ElementType = 'div'>({
  as,
  hasStatus = false,
  isDisabled = false,
  name,
  shape = 'circle',
  size = 'medium',
  source,
  status,
  type: initialType = 'image',
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, AvatarProps>) => {
  // If source is not provided, fallback to icon
  const type =
    initialType === 'image' && (source === undefined || source === '')
      ? 'icon'
      : initialType;

  const Component: React.ElementType = as ?? 'div';
  const isButton = Component === 'button';
  const classes = classNames(
    `ndl-avatar ndl-avatar-${shape} ndl-avatar-${size}`,
    className,
  );
  const shapeClasses = classNames(
    `ndl-avatar-shape ndl-avatar-${shape} ndl-avatar-${shape}-${size}`,
    {
      'ndl-avatar-disabled': isDisabled,
    },
  );

  const typeClass = `ndl-avatar-${type}`;

  const renderImageType = () => (
    <>
      <img data-testid="ndl-avatar-image" src={source} alt={name} />
      <div
        data-testid="ndl-avatar-image-overlay"
        className={`${shapeClasses} ndl-avatar-image-overlay`}
      />
    </>
  );

  const renderIconType = () => <UserIconOutline />;

  const renderLettersType = () => {
    return (
      <Typography variant="subheading-small" className="ndl-avatar-typography">
        {getLettersDisplay(name ?? '')}
      </Typography>
    );
  };

  const TypeComponent =
    type === 'image'
      ? renderImageType()
      : type === 'icon'
        ? renderIconType()
        : type === 'letters' && Boolean(name)
          ? renderLettersType()
          : null;

  const isStatusIndicatorVisible =
    hasStatus && !isDisabled && status !== undefined;

  return (
    <Component
      ref={ref}
      className={classes}
      style={style}
      aria-hidden={!isButton}
      {...(isButton && {
        disabled: isDisabled,
      })}
      aria-label={`Avatar ${name}`}
      {...restProps}
      {...htmlAttributes}
    >
      <div className={classNames(shapeClasses, typeClass)}>{TypeComponent}</div>
      {isStatusIndicatorVisible && (
        <StatusIndicator size={size} status={status} />
      )}
    </Component>
  );
};
